/**
 * caption slice: 자막/음성 텍스트 관리
 * wehago-conference의 redux/modules/caption + speechText 에서 리팩토링
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { CaptionEntry, TranscriptionMode } from '@/types/store';
import { toAbsoluteProfileUrl } from '@/utils/profileUtils';

// re-export for backwards compatibility
export type { CaptionEntry, TranscriptionMode } from '@/types/store';

export interface CaptionState {
  isEnabled: boolean;
  entries: CaptionEntry[];
  /** 화자별 interim 진행 중 텍스트 */
  currentTexts: Record<string, CaptionCurrentText>;
  /** @deprecated currentTexts 중 가장 최근 값. 기존 컴포넌트 호환용 */
  currentSpeakerId: string | null;
  /** @deprecated currentTexts 중 가장 최근 값. 기존 컴포넌트 호환용 */
  currentText: string;
  /** interim 진행 중 부분 번역 결과 (내 언어 기준) */
  currentTranslatedText: string;
  /** interim 진행 중 다국어 번역 결과 */
  currentMultiLangData: Record<string, string> | null;
  language: string;
  /** 현재 사용 중인 STT 모드 (speechRecognition: 구형 호환, jigasi: 서버 측 Jigasi) */
  transcriptionMode: TranscriptionMode;
  /** Jigasi transcriber가 방에 참여했는지 여부 */
  isTranscriberJoined: boolean;
  /** Jigasi dial() 호출 진행 중 (같은 클라이언트 race condition 방지) */
  isTranscriberDialPending: boolean;
  /** Jigasi transcriber의 JID */
  transcriberJID: string | null;
}

export interface CaptionCurrentText {
  speakerId: string;
  speakerName: string;
  nickname: string;
  profileUrl: string;
  text: string;
  translatedText: string;
  multiLangData: Record<string, string> | null;
  updatedAt: number;
}

const initialState: CaptionState = {
  isEnabled: false,
  entries: [],
  currentTexts: {},
  currentSpeakerId: null,
  currentText: '',
  currentTranslatedText: '',
  currentMultiLangData: null,
  language: 'ko',
  transcriptionMode: 'none',
  isTranscriberJoined: false,
  isTranscriberDialPending: false,
  transcriberJID: null,
};

const normalizeProfileUrl = (url?: string | null) => toAbsoluteProfileUrl(url) ?? '';

const normalizeCaptionEntry = (entry: CaptionEntry): CaptionEntry => ({
  ...entry,
  profileUrl: normalizeProfileUrl(entry.profileUrl),
});

const syncLatestCurrentText = (state: CaptionState) => {
  const latest = Object.values(state.currentTexts).reduce<CaptionCurrentText | null>(
    (currentLatest, item) => {
      if (!currentLatest || item.updatedAt >= currentLatest.updatedAt) {
        return item;
      }
      return currentLatest;
    },
    null
  );

  if (!latest) {
    state.currentSpeakerId = null;
    state.currentText = '';
    state.currentTranslatedText = '';
    state.currentMultiLangData = null;
    return;
  }

  state.currentSpeakerId = latest.speakerId;
  state.currentText = latest.text;
  state.currentTranslatedText = latest.translatedText;
  state.currentMultiLangData = latest.multiLangData;
};

const clearFinalizedCurrentText = (state: CaptionState, entry: CaptionEntry) => {
  if (!entry.isFinal || entry.isSystem) return;
  delete state.currentTexts[entry.userId];
  syncLatestCurrentText(state);
};

const captionSlice = createSlice({
  name: 'caption',
  initialState,
  reducers: {
    setEnabled(state, action: PayloadAction<boolean>) {
      state.isEnabled = action.payload;
    },

    addEntry(state, action: PayloadAction<CaptionEntry>) {
      const entry = normalizeCaptionEntry(action.payload);
      state.entries.push(entry);
      clearFinalizedCurrentText(state, entry);
    },

    /**
     * 이전 STT 히스토리를 현재 라이브 엔트리와 병합한다.
     */
    mergeHistoryEntries(state, action: PayloadAction<CaptionEntry[]>) {
      const DUP_WINDOW_MS = 2 * 60 * 1000;
      const isDupOfExisting = (h: CaptionEntry) =>
        state.entries.some(
          (e) =>
            e.text === h.text &&
            (e.userId === h.userId || (!!e.userName && e.userName === h.userName)) &&
            Math.abs(e.timestamp - h.timestamp) < DUP_WINDOW_MS
        );

      const byId = new Map<string, CaptionEntry>();
      [...action.payload.filter((h) => !isDupOfExisting(h)), ...state.entries].forEach((entry) => {
        byId.set(entry.id, normalizeCaptionEntry(entry));
      });
      state.entries = Array.from(byId.values())
        .sort((a, b) => a.timestamp - b.timestamp);
    },

    /** 같은 id가 있으면 text/isFinal 업데이트, 없으면 새로 추가 (Jigasi interim→final 전환용) */
    upsertEntry(state, action: PayloadAction<CaptionEntry>) {
      const entry = normalizeCaptionEntry(action.payload);
      const idx = state.entries.findIndex((e) => e.id === entry.id);
      if (idx >= 0) {
        state.entries[idx] = { ...state.entries[idx], ...entry };
      } else {
        state.entries.push(entry);
      }
      clearFinalizedCurrentText(state, entry);
    },

    setCurrentText(
      state,
      action: PayloadAction<{
        speakerId: string;
        text: string;
        speakerName?: string;
        nickname?: string;
        profileUrl?: string;
        updatedAt?: number;
      }>
    ) {
      const previous = state.currentTexts[action.payload.speakerId];
      state.currentTexts[action.payload.speakerId] = {
        speakerId: action.payload.speakerId,
        speakerName: action.payload.speakerName ?? previous?.speakerName ?? action.payload.speakerId,
        nickname: action.payload.nickname ?? previous?.nickname ?? '',
        profileUrl: action.payload.profileUrl !== undefined
          ? normalizeProfileUrl(action.payload.profileUrl)
          : previous?.profileUrl ?? '',
        text: action.payload.text,
        translatedText: previous?.translatedText ?? '',
        multiLangData: previous?.multiLangData ?? null,
        updatedAt: action.payload.updatedAt ?? Date.now(),
      };
      syncLatestCurrentText(state);
    },

    clearCurrentText(state, action: PayloadAction<string | undefined>) {
      if (action.payload) {
        delete state.currentTexts[action.payload];
      } else {
        state.currentTexts = {};
      }
      syncLatestCurrentText(state);
    },

    /** interim 진행 중 부분 번역 결과 업데이트 */
    setCurrentTranslation(
      state,
      action: PayloadAction<{
        speakerId?: string;
        translatedText: string;
        multiLangData: Record<string, string> | null;
      }>
    ) {
      const speakerId = action.payload.speakerId ?? state.currentSpeakerId;
      if (speakerId && state.currentTexts[speakerId]) {
        state.currentTexts[speakerId].translatedText = action.payload.translatedText;
        state.currentTexts[speakerId].multiLangData = action.payload.multiLangData;
      }
      syncLatestCurrentText(state);
    },

    setLanguage(state, action: PayloadAction<string>) {
      state.language = action.payload;
    },

    /** 특정 엔트리의 번역 상태를 업데이트 */
    updateEntryTranslation(
      state,
      action: PayloadAction<{
        id: string;
        translatedText?: string | null;
        isTranslating?: boolean;
        multiLangData?: Record<string, string> | null;
      }>
    ) {
      const entry = state.entries.find((e) => e.id === action.payload.id);
      if (entry) {
        if (action.payload.translatedText !== undefined) {
          entry.translatedText = action.payload.translatedText;
        }
        if (action.payload.multiLangData !== undefined) {
          entry.multiLangData = action.payload.multiLangData;
        }
        if (action.payload.isTranslating !== undefined) {
          entry.isTranslating = action.payload.isTranslating;
        }
      }
    },

    setTranscriptionMode(state, action: PayloadAction<TranscriptionMode>) {
      state.transcriptionMode = action.payload;
    },

    setTranscriberJoined(
      state,
      action: PayloadAction<{ joined: boolean; jid: string | null }>
    ) {
      state.isTranscriberJoined = action.payload.joined;
      state.transcriberJID = action.payload.jid;
      if (action.payload.joined) {
        state.isTranscriberDialPending = false;
      }
    },

    setTranscriberDialPending(state, action: PayloadAction<boolean>) {
      state.isTranscriberDialPending = action.payload;
    },

    reset() {
      return initialState;
    },
  },
});

export const captionActions = captionSlice.actions;
export default captionSlice.reducer;
